import type { TFunction } from 'i18next';
import { RotateCcw, Sparkles } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DateField } from '@/components/ui/date-field';
import { Input } from '@/components/ui/input';
import { formatAmount, parseAmountInput } from '../money';
import {
  asksVehicle,
  formKind,
  holdsProposal,
  isRequiredByType,
  isShown,
  mileageTotal,
  missingFields,
  restoreProposal,
  withSupplierRefreshed,
  withSupplierRules,
  type ExpenseForm,
  type Proposals,
  type TaxLineDraft,
} from './form-model';
import {
  CategorySelect,
  DocumentTypeSelect,
  FieldShell,
  MileageTotalField,
  PaymentControl,
  type FieldKey,
  type FieldOptions,
} from './ExpenseFieldParts';
import { EuroInput } from './EuroInput';
import { VehiclePicker } from './ReferencePickers';
import { asksSite, SiteSelect } from '../suppliers/picker/SiteSelect';
import { StayPropertyField } from '../suppliers/picker/StayPropertyField';
import { SupplierPicker, useChosenSupplier } from '../suppliers/picker/SupplierPicker';
import { AiNotice } from './PrefillNotice';
import { TaxLinesSheet } from './TaxLinesSheet';
import { TotalsLine } from './TotalsLine';

type FieldProps = {
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  readonly onChange: (next: ExpenseForm) => void;
  /** The proposals moved to the chosen supplier's new name and VAT number, changed in its panel. */
  readonly onProposalsChange?: ((next: Proposals) => void) | undefined;
  /** Whose cards and vehicles to offer: the expense's owner, undefined for the caller. */
  readonly listOwner?: string | undefined;
  /** The categories the chosen note's type accepts; none when no note (or an R2 note) is chosen. */
  readonly allowedCategories?: ReadonlySet<string> | null | undefined;
};

/** What the sections of the pane share: the form, the ways to change it, and the field builders. */
type FieldApi = {
  readonly uid: string;
  readonly props: FieldProps;
  readonly emit: (next: ExpenseForm) => void;
  readonly set: (patch: Partial<ExpenseForm>) => void;
  readonly touch: (key: string) => () => void;
  readonly invalid: (key: FieldKey) => boolean;
  readonly field: (key: FieldKey, control: ReactNode, options?: FieldOptions) => ReactNode;
  readonly text: (key: FieldKey, props?: Partial<React.ComponentProps<'input'>>) => ReactNode;
  readonly amount: (key: 'total' | 'nonTaxable') => ReactNode;
  /** The date field's text, and the remount «Svuota» uses to wipe a half-typed date too. */
  readonly date: {
    readonly text: string;
    readonly setText: (text: string) => void;
    readonly epoch: number;
  };
};

function useFieldApi(props: FieldProps): FieldApi {
  const { form, proposals, onChange } = props;
  const uid = useId();
  /** Fields left once: a required one empty after that shows its error inline. */
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [dateText, setDateText] = useState('');
  const [dateEpoch, setDateEpoch] = useState(0);
  const missing = new Set(missingFields(form));
  /** Every change goes here, through the supplier's rules (`withSupplierRules`). */
  const emit = (next: ExpenseForm): void => onChange(withSupplierRules(form, next));
  const set = (patch: Partial<ExpenseForm>): void => emit({ ...form, ...patch });
  const touch = (key: string) => (): void => setTouched((prev) => new Set(prev).add(key));
  const invalid = (key: FieldKey): boolean =>
    isRequiredByType(form, key) && touched.has(key) && missing.has(key);
  const field: FieldApi['field'] = (key, control, options = {}) => (
    <FieldShell
      {...{ fieldKey: key, uid, form, proposals, control, options }}
      showError={touched.has(key) && missing.has(key)}
      onRestore={(proposable) => emit(restoreProposal(form, proposable, proposals))}
      onClear={(clearKey) => {
        if (clearKey === 'date') setDateEpoch((epoch) => epoch + 1);
        set({ [clearKey]: '' });
      }}
    />
  );
  const text: FieldApi['text'] = (key, extra = {}) => {
    const required = isRequiredByType(form, key);
    return (
      <Input
        id={`${uid}-${key}`}
        value={form[key] as string}
        onChange={(e) => set({ [key]: e.target.value })}
        onBlur={touch(key)}
        aria-required={required || undefined}
        aria-invalid={invalid(key) || undefined}
        aria-describedby={invalid(key) ? `${uid}-${key}-error` : undefined}
        {...extra}
      />
    );
  };
  const amount: FieldApi['amount'] = (key) => (
    <EuroInput
      id={`${uid}-${key}`}
      value={form[key]}
      aria-invalid={form[key] !== '' && parseAmountInput(form[key]) === null}
      onChange={(e) => set({ [key]: e.target.value })}
    />
  );
  const date = { text: dateText, setText: setDateText, epoch: dateEpoch };
  return { uid, props, emit, set, touch, invalid, field, text, amount, date };
}

/** The date, amount, route, mileage, supplier and document fields. */
function MainFields({ api }: { readonly api: FieldApi }) {
  const { t } = useTranslation('expenses');
  const { uid, field, text, amount, set, touch, invalid, date } = api;
  const { form, listOwner } = api.props;
  const kind = formKind(form);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {field(
        'date',
        <DateField
          key={date.epoch}
          id={`${uid}-date`}
          value={form.date}
          onChange={(next) => set({ date: next })}
          onTextChange={date.setText}
          onBlur={touch('date')}
          aria-required={isRequiredByType(form, 'date') || undefined}
          aria-invalid={invalid('date') || undefined}
          aria-describedby={invalid('date') ? `${uid}-date-error` : undefined}
        />,
        { proposable: true, filled: date.text !== '' },
      )}
      {kind !== 'mileage' && field('total', amount('total'), { proposable: true })}
      {isShown(form, 'route') &&
        field('route', text('route', { placeholder: t('capture.fields.routePlaceholder') }), {
          className: 'sm:col-span-2',
        })}
      {kind === 'mileage' && (
        <>
          {field('km', text('km', { inputMode: 'decimal' }))}
          {field('aciRate', text('aciRate', { inputMode: 'decimal' }))}
          <MileageTotalField uid={uid} total={mileageTotal(form)} />
        </>
      )}
      {isShown(form, 'supplierName') && <SupplierField api={api} />}
      {isShown(form, 'documentNumber') &&
        field('documentNumber', text('documentNumber'), { proposable: true })}
      {isShown(form, 'documentType') &&
        field('documentType', <DocumentTypeSelect uid={uid} form={form} set={set} />, {
          proposable: true,
          className: 'sm:col-span-2',
        })}
      {asksVehicle(form) && (
        <div className="sm:col-span-2">
          <VehiclePicker
            owner={listOwner}
            value={form.vehicleId}
            storedLabel={form.vehicleLabel}
            onChange={(vehicleId, vehicleLabel) => set({ vehicleId, vehicleLabel })}
          />
        </div>
      )}
    </div>
  );
}

/**
 * «Fornitore» (#958 F, boards 1.1–1.13): the catalog's picker; under it, the chosen supplier's site
 * when it has several, or «Dove hai dormito?» when it is an intermediary. The VAT number belongs to
 * the supplier now: it comes from the document or the create window, not from a field of its own.
 */
function SupplierField({ api }: { readonly api: FieldApi }) {
  const { uid, field, set, touch, invalid } = api;
  const { form, proposals } = api.props;
  const { state: chosen, adopt } = useChosenSupplier(form.supplierId);
  const supplier = chosen.status === 'ready' ? chosen.supplier : null;
  return (
    <>
      {field(
        'supplierName',
        <SupplierPicker
          id={`${uid}-supplierName`}
          value={form}
          chosen={chosen}
          documentVat={(proposals.supplierVat?.value as string | undefined) ?? null}
          // Typed text keeps a document's VAT number only while nothing was chosen.
          onType={(supplierName) =>
            set(form.supplierId === '' ? { supplierName } : { supplierName, supplierVat: '' })
          }
          onChoose={(choice) =>
            set({
              supplierId: choice.supplierId,
              supplierName: choice.supplierName,
              supplierVat: choice.supplierVat,
              siteId: choice.siteId ?? '',
            })
          }
          onAdopt={adopt}
          onRefresh={({ supplierName, supplierVat }) => {
            const next = withSupplierRefreshed(form, proposals, supplierName, supplierVat);
            if (next.proposals !== proposals) api.props.onProposalsChange?.(next.proposals);
            api.props.onChange(next.form);
          }}
          onClear={() => set({ supplierId: '', supplierName: '', supplierVat: '' })}
          onBlur={touch('supplierName')}
          invalid={invalid('supplierName')}
          describedBy={invalid('supplierName') ? `${uid}-supplierName-error` : undefined}
        />,
        { proposable: true, ownClear: true, className: 'sm:col-span-2' },
      )}
      {supplier && !supplier.intermediary && asksSite(supplier, form.siteId) && (
        <div className="sm:col-span-2" data-field="siteId">
          <SiteSelect
            id={`${uid}-siteId`}
            supplier={supplier}
            value={form.siteId}
            place={form.place}
            onChange={(siteId) => set({ siteId })}
            onAdopt={adopt}
          />
        </div>
      )}
      {supplier?.intermediary && (
        <div className="sm:col-span-2">
          <StayPropertyField
            id={`${uid}-stayProperty`}
            intermediaryName={supplier.name}
            value={form.stayPropertySiteId}
            label={form.stayPropertyLabel}
            onChange={(stayPropertySiteId, stayPropertyLabel) =>
              set({ stayPropertySiteId, stayPropertyLabel })
            }
          />
        </div>
      )}
    </>
  );
}

/** Where, why and who: the free-text fields under the payment. */
function DetailFields({ api }: { readonly api: FieldApi }) {
  const { field, text } = api;
  const { form } = api.props;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {isShown(form, 'referenceDays') &&
        field('referenceDays', text('referenceDays', { inputMode: 'numeric' }))}
      {field('place', text('place'), { proposable: true })}
      {field('businessReason', text('businessReason'))}
      {field('peopleInvolved', text('peopleInvolved'))}
      {field('description', text('description'), {
        className: isShown(form, 'referenceDays') ? 'sm:col-span-2' : undefined,
      })}
    </div>
  );
}

/**
 * The right pane (design §1.3 step 3, §1.4): every field of release 1, the ✦ on what the document
 * proposed (with «da verificare» when the reading was uncertain), «↺» to put a proposal back and
 * «✕» to clear. `owner` is «Per conto di» (administrators only), drawn first.
 */
export function ExpenseFields(props: FieldProps & { readonly owner?: ReactNode }) {
  const { t } = useTranslation('expenses');
  const [sheetOpen, setSheetOpen] = useState(false);
  const api = useFieldApi(props);
  const { uid, field, set, emit } = api;
  const { form, proposals, owner, listOwner } = props;
  const anyProposal = Object.keys(proposals).length > 0;
  const anyUncertain = Object.values(proposals).some((p) => p?.uncertain);
  return (
    <section aria-labelledby={`${uid}-heading`} className="space-y-4">
      <h2 id={`${uid}-heading`} className="text-base font-semibold">
        {t('capture.fields.heading')}
      </h2>
      {anyProposal && <AiNotice uncertain={anyUncertain} />}

      {owner}

      {field(
        'category',
        <CategorySelect uid={uid} form={form} set={set} allowed={props.allowedCategories} />,
        { proposable: true },
      )}

      <MainFields api={api} />

      {isShown(form, 'taxLines') && (
        <TaxSummary
          form={form}
          proposals={proposals}
          onOpen={() => setSheetOpen(true)}
          onRestore={() => emit(restoreProposal(form, 'taxLines', proposals))}
        />
      )}

      {isShown(form, 'payment') &&
        field(
          'paymentWay',
          <PaymentControl uid={uid} form={form} set={set} listOwner={listOwner} />,
          { proposable: true, group: true },
        )}

      <DetailFields api={api} />

      <TaxLinesSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        form={form}
        onChange={(patch) => set(patch)}
      />
    </section>
  );
}

function lineSummary(t: TFunction<'expenses'>, line: TaxLineDraft): string {
  const money = (value: string): string => {
    const parsed = parseAmountInput(value);
    return parsed === null ? value || '—' : formatAmount(parsed);
  };
  const label = t(`capture.taxTypes.${line.type}`);
  if (line.mode === 'fixed') return t('capture.taxSummary.fixed', { label, tax: money(line.tax) });
  if (line.mode === 'exempt')
    return t('capture.taxSummary.exempt', { label, nature: line.nature, base: money(line.base) });
  return t('capture.taxSummary.rate', {
    label,
    rate: line.rate || '—',
    base: money(line.base),
    tax: money(line.tax),
  });
}

/** The lines in one row each, the totals check, and «Modifica righe» (design §1.4). */
function TaxSummary({
  form,
  proposals,
  onOpen,
  onRestore,
}: {
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  readonly onOpen: () => void;
  readonly onRestore: () => void;
}) {
  const { t } = useTranslation('expenses');
  const { t: tDetail } = useTranslation('expenses-detail');
  const holds = holdsProposal(form, 'taxLines', proposals);
  const proposal = proposals.taxLines;
  return (
    <div className="space-y-1 rounded-lg border border-border p-3" data-field="taxLines">
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium">{t('capture.fields.taxLines')}</span>
        {holds && (
          <Sparkles
            role="img"
            aria-label={t('capture.prefill.proposed')}
            className="size-3.5 text-brand"
          />
        )}
        {holds && proposal?.uncertain && (
          <span className="rounded bg-warning-tint px-1 text-xs">
            {t('capture.prefill.uncertain')}
          </span>
        )}
        {holds && proposal && !proposal.uncertain && (
          <span className="rounded bg-muted px-1 text-xs">{tDetail('ai.sure')}</span>
        )}
        {proposal && !holds && (
          <>
            <span className="text-xs text-muted-foreground">{t('capture.prefill.modified')}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 min-w-11 px-1"
              aria-label={t('capture.prefill.restore', { field: t('capture.fields.taxLines') })}
              onClick={onRestore}
            >
              <RotateCcw />
            </Button>
          </>
        )}
        <span className="grow" />
        <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={onOpen}>
          {t('capture.fields.editTaxLines')}
        </Button>
      </div>
      {form.taxLines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('capture.fields.taxLinesNone')}</p>
      ) : (
        <ul className="text-sm tabular-nums">
          {form.taxLines.map((line) => (
            <li key={line.key}>{lineSummary(t, line)}</li>
          ))}
        </ul>
      )}
      <TotalsLine form={form} />
    </div>
  );
}
